// Initial setup wizard for scheduling profile
import React, { useState } from 'react';
import { setupSchedulingProfile } from '../../services/scheduling-api';

interface SchedulingSetupProps {
  onComplete: () => void;
}

const TIMEZONES = [
  { value: 'America/Toronto', label: 'Eastern Time (Toronto)' },
  { value: 'America/New_York', label: 'Eastern Time (New York)' },
  { value: 'America/Chicago', label: 'Central Time (Chicago)' },
  { value: 'America/Denver', label: 'Mountain Time (Denver)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
  { value: 'America/Vancouver', label: 'Pacific Time (Vancouver)' },
  { value: 'America/Edmonton', label: 'Mountain Time (Edmonton)' },
  { value: 'America/Winnipeg', label: 'Central Time (Winnipeg)' },
  { value: 'America/Halifax', label: 'Atlantic Time (Halifax)' },
  { value: 'Europe/London', label: 'GMT (London)' },
  { value: 'Europe/Paris', label: 'CET (Paris)' },
  { value: 'UTC', label: 'UTC' }
];

const SchedulingSetup: React.FC<SchedulingSetupProps> = ({ onComplete }) => {
  const [step, setStep] = useState(1);
  const [subdomain, setSubdomain] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [timezone, setTimezone] = useState('America/Toronto');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [bookingUrl, setBookingUrl] = useState('');

  const validateSubdomain = (value: string): string => {
    if (!value) return 'Subdomain is required';
    if (value.length < 3) return 'Subdomain must be at least 3 characters';
    if (value.length > 30) return 'Subdomain must be 30 characters or less';
    if (!/^[a-z0-9-]+$/.test(value)) return 'Only lowercase letters, numbers, and hyphens allowed';
    if (value.startsWith('-') || value.endsWith('-')) return 'Cannot start or end with a hyphen';
    return '';
  };

  const handleSubdomainChange = (value: string) => {
    const cleaned = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSubdomain(cleaned);
    setError(validateSubdomain(cleaned));
  };

  const handleSubmit = async () => {
    const validationError = validateSubdomain(subdomain);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await setupSchedulingProfile({
        subdomain,
        displayName: displayName || undefined,
        timezone
      });

      setBookingUrl(result.bookingUrl);
      setStep(3);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to setup profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      maxWidth: '600px',
      margin: '0 auto',
      padding: '40px 20px'
    }}>
      {/* Progress indicator */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '40px', gap: '10px' }}>
        {[1, 2, 3].map(s => (
          <div
            key={s}
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: step >= s ? '#3b82f6' : '#e5e7eb'
            }}
          />
        ))}
      </div>

      {/* Step 1: Subdomain */}
      {step === 1 && (
        <div>
          <h1 style={{ margin: '0 0 10px', fontSize: '1.75rem', textAlign: 'center' }}>
            Create Your Booking Page
          </h1>
          <p style={{ margin: '0 0 40px', color: '#666', textAlign: 'center' }}>
            Choose a unique URL for your booking page
          </p>

          <div style={{ marginBottom: '30px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
              Your booking URL
            </label>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              overflow: 'hidden'
            }}>
              <span style={{ padding: '12px 15px', backgroundColor: '#f3f4f6', color: '#666', borderRight: '1px solid #d1d5db' }}>
                https://
              </span>
              <input
                type="text"
                value={subdomain}
                onChange={e => handleSubdomainChange(e.target.value)}
                placeholder="yourname"
                style={{
                  flex: 1,
                  padding: '12px 15px',
                  border: 'none',
                  outline: 'none',
                  fontSize: '1rem',
                  backgroundColor: 'transparent'
                }}
              />
              <span style={{ padding: '12px 15px', backgroundColor: '#f3f4f6', color: '#666', borderLeft: '1px solid #d1d5db' }}>
                .cal.larryshelp.org
              </span>
            </div>
            {error && (
              <p className="error-message" style={{ marginTop: '10px' }}>{error}</p>
            )}
          </div>

          <button
            onClick={() => {
              if (!validateSubdomain(subdomain)) {
                setStep(2);
              }
            }}
            disabled={!!validateSubdomain(subdomain)}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: validateSubdomain(subdomain) ? '#9ca3af' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: validateSubdomain(subdomain) ? 'not-allowed' : 'pointer'
            }}
          >
            Continue
          </button>
        </div>
      )}

      {/* Step 2: Profile Details */}
      {step === 2 && (
        <div>
          <h1 style={{ margin: '0 0 10px', fontSize: '1.75rem', textAlign: 'center' }}>
            Your Profile
          </h1>
          <p style={{ margin: '0 0 40px', color: '#666', textAlign: 'center' }}>
            Add some details (you can change these later)
          </p>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Display Name
            </label>
            <input
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="Your Name"
              style={{
                width: '100%',
                padding: '12px 15px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '1rem'
              }}
            />
          </div>

          <div style={{ marginBottom: '30px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Timezone
            </label>
            <select
              value={timezone}
              onChange={e => setTimezone(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 15px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '1rem'
              }}
            >
              {TIMEZONES.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </div>

          {error && (
            <p className="error-message">{error}</p>
          )}

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setStep(1)}
              style={{
                flex: 1,
                padding: '14px',
                backgroundColor: '#f3f4f6',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                flex: 2,
                padding: '14px',
                backgroundColor: loading ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Creating...' : 'Create Booking Page'}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Success */}
      {step === 3 && (
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '80px',
            height: '80px',
            margin: '0 auto 30px',
            backgroundColor: '#dcfce7',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <span style={{ fontSize: '2.5rem' }}>✓</span>
          </div>

          <h1 style={{ margin: '0 0 10px', fontSize: '1.75rem' }}>
            You're all set!
          </h1>
          <p style={{ margin: '0 0 30px', color: '#666' }}>
            Your booking page is ready
          </p>

          <div style={{
            padding: '20px',
            backgroundColor: '#f3f4f6',
            borderRadius: '8px',
            marginBottom: '30px'
          }}>
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#666' }}>Your booking URL</p>
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '1.125rem', color: '#3b82f6', fontWeight: '500' }}
            >
              {bookingUrl}
            </a>
          </div>

          <button
            onClick={onComplete}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Go to Dashboard
          </button>
        </div>
      )}
    </div>
  );
};

export default SchedulingSetup;
